import type {
  CreateEnquiryRequest,
  CreateEnquiryResponseDto,
  CreateWalkerRegistrationRequest,
  ReorderWalkerPhotoItem,
  WalkerCardDto,
  WalkerDetailDto,
  WalkerPhotoDto,
  WalkerRegistrationResultDto,
} from "../types/walkers";
import type {
  BookingProposalDto,
  ConversationRebookRequestDto,
  ConversationThreadDto,
  CreateBookingProposalCheckoutSessionByTokenRequest,
  CreateBookingProposalCheckoutSessionRequest,
  CreateCheckoutSessionResponseDto,
  CreateConversationBookingProposalRequest,
  CreateConversationRebookRequestRequest,
  OwnerBookingListItemDto,
  OwnerConversationAccessResponseDto,
  ReplyToConversationRequest,
  ReplyToConversationResponseDto,
  RequestOwnerConversationAccessRequest,
  WalkerConversationListItemDto,
} from "../types/conversations";
import type {
  ConsumeWalkerMagicLinkRequest,
  RequestWalkerMagicLinkRequest,
  WalkerSessionDto,
} from "../types/auth";

export type AdminStaleConversationDto = {
  conversationId: number;
  walkerProfileId: number;
  userId: number;
  ownerEmail: string;
  ownerName?: string | null;
  status: string;
  statusId: number;
  createdUtc: string;
  lastMessageUtc: string;
  closedUtc?: string | null;
};

export type AdminAutoCloseStaleConversationsResponseDto = {
  success: boolean;
  staleAfterDays: number;
  rowsAffected: number;
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.trim() || "https://localhost:7232";

const ADMIN_API_KEY =
  process.env.REACT_APP_ADMIN_API_KEY?.trim() ||
  "89915a9a-08fd-473b-afde-f92e10f71ef7";

function normalizeUrl(url?: string | null): string {
  if (!url) return "";

  const trimmed = url.trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${API_BASE}${trimmed}`;
  }

  return `${API_BASE}/${trimmed}`;
}

function normalizeWalkerPhoto(photo: WalkerPhotoDto): WalkerPhotoDto {
  return {
    ...photo,
    url: normalizeUrl(photo.url),
  };
}

function normalizeWalkerDetail(detail: WalkerDetailDto): WalkerDetailDto {
  return {
    ...detail,
    photos: Array.isArray(detail.photos)
      ? detail.photos.map(normalizeWalkerPhoto)
      : [],
  };
}

function normalizeConversationRebookRequest(
  request: ConversationRebookRequestDto
): ConversationRebookRequestDto {
  return {
    ...request,
    ownerEmail: request.ownerEmail ?? "",
    ownerName: request.ownerName ?? null,
    requestMessage: request.requestMessage ?? null,
    proposalBookingProposalId: request.proposalBookingProposalId ?? null,
    viewedByWalkerUtc: request.viewedByWalkerUtc ?? null,
    dismissedUtc: request.dismissedUtc ?? null,
    proposalCreatedUtc: request.proposalCreatedUtc ?? null,
    updatedUtc: request.updatedUtc ?? null,
  };
}

function normalizeConversationThread(
  thread: ConversationThreadDto
): ConversationThreadDto {
  return {
    ...thread,
    messages: Array.isArray(thread.messages) ? thread.messages : [],
    rebookRequests: Array.isArray(thread.rebookRequests)
      ? thread.rebookRequests.map(normalizeConversationRebookRequest)
      : [],
  };
}

function normalizeWalkerConversationListItem(
  item: WalkerConversationListItemDto
): WalkerConversationListItemDto {
  return {
    ...item,
    ownerEmail: item.ownerEmail ?? "",
    ownerName: item.ownerName ?? null,
    lastMessagePreview: item.lastMessagePreview ?? null,
    walkerEmail: item.walkerEmail ?? null,
    walkerReplyToken: item.walkerReplyToken ?? null,
    walkerReplyTokenExpiresUtc: item.walkerReplyTokenExpiresUtc ?? null,
    hasActiveRebookRequest: Boolean(item.hasActiveRebookRequest),
    activeRebookRequestCount: item.activeRebookRequestCount ?? 0,
    latestRebookRequestedUtc: item.latestRebookRequestedUtc ?? null,
    latestRebookRequestMessage: item.latestRebookRequestMessage ?? null,
  };
}

function normalizeBookingProposal(
  proposal: BookingProposalDto
): BookingProposalDto {
  return {
    ...proposal,
    ownerEmail: proposal.ownerEmail ?? "",
    serviceType: proposal.serviceType?.trim() || "Booking proposal",
    currency: proposal.currency?.trim()?.toLowerCase() || "gbp",
    locationSummary: proposal.locationSummary ?? null,
    notes: proposal.notes ?? null,
    stripeCheckoutSessionId: proposal.stripeCheckoutSessionId ?? null,
    stripePaymentIntentId: proposal.stripePaymentIntentId ?? null,
    paidUtc: proposal.paidUtc ?? null,
    completedUtc: proposal.completedUtc ?? null,
    cancelledUtc: proposal.cancelledUtc ?? null,
    expiresUtc: proposal.expiresUtc ?? null,
    updatedUtc: proposal.updatedUtc ?? null,
  };
}

function normalizeOwnerBooking(
  item: OwnerBookingListItemDto
): OwnerBookingListItemDto {
  return {
    ...item,
    ownerEmail: item.ownerEmail ?? "",
    serviceType: item.serviceType?.trim() || "Booking proposal",
    currency: item.currency?.trim()?.toLowerCase() || "gbp",
    locationSummary: item.locationSummary ?? null,
    notes: item.notes ?? null,
    stripeCheckoutSessionId: item.stripeCheckoutSessionId ?? null,
    stripePaymentIntentId: item.stripePaymentIntentId ?? null,
    paidUtc: item.paidUtc ?? null,
    completedUtc: item.completedUtc ?? null,
    cancelledUtc: item.cancelledUtc ?? null,
    expiresUtc: item.expiresUtc ?? null,
    updatedUtc: item.updatedUtc ?? null,
    walkerDisplayTitle: item.walkerDisplayTitle ?? null,
    walkerFirstName: item.walkerFirstName ?? null,
    walkerLastName: item.walkerLastName ?? null,
    walkerSlug: item.walkerSlug ?? null,
    ownerReplyToken: item.ownerReplyToken ?? null,
    ownerReplyTokenExpiresUtc: item.ownerReplyTokenExpiresUtc ?? null,
  };
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }

  return (await res.json()) as T;
}

async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });

  return readJson<T>(res);
}

async function fetchJsonWithAuth<T>(
  input: RequestInfo,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(input, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });

  return readJson<T>(res);
}

async function fetchNoContentWithAuth(
  input: RequestInfo,
  init?: RequestInit
): Promise<void> {
  const res = await fetch(input, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
}

function buildAdminHeaders(extra?: HeadersInit): HeadersInit {
  return {
    ...(ADMIN_API_KEY ? { "X-Admin-Key": ADMIN_API_KEY } : {}),
    ...(extra ?? {}),
  };
}

export const walkiesClient = {
  async searchWalkers(postcode: string, maxRows = 20): Promise<WalkerCardDto[]> {
    const qs = new URLSearchParams({
      postcode,
      maxRows: String(maxRows),
    });

    return fetchJson<WalkerCardDto[]>(
      `${API_BASE}/api/Walkers/search?${qs.toString()}`,
      {
        method: "GET",
      }
    );
  },

  async getWalkerBySlug(slug: string): Promise<WalkerDetailDto> {
    const encodedSlug = encodeURIComponent(slug.trim());

    const data = await fetchJson<WalkerDetailDto>(
      `${API_BASE}/api/Walkers/${encodedSlug}`,
      {
        method: "GET",
      }
    );

    return normalizeWalkerDetail(data);
  },

  async createEnquiry(
    request: CreateEnquiryRequest
  ): Promise<CreateEnquiryResponseDto> {
    return fetchJson<CreateEnquiryResponseDto>(`${API_BASE}/api/Enquiries`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });
  },

  async getConversationByToken(token: string): Promise<ConversationThreadDto> {
    const encodedToken = encodeURIComponent(token.trim());

    const data = await fetchJson<ConversationThreadDto>(
      `${API_BASE}/api/Conversations/${encodedToken}`,
      {
        method: "GET",
      }
    );

    return normalizeConversationThread(data);
  },

  async replyToConversation(
    request: ReplyToConversationRequest
  ): Promise<ReplyToConversationResponseDto> {
    const payload: ReplyToConversationRequest = {
      ...request,
      token: request.token?.trim() ?? request.token,
      message: request.message?.trim() ?? request.message,
    };

    const data = await fetchJson<ReplyToConversationResponseDto>(
      `${API_BASE}/api/Conversations/reply`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    return {
      ...data,
      conversation: data.conversation
        ? normalizeConversationThread(data.conversation)
        : null,
    };
  },

  async createConversationRebookRequest(
    request: CreateConversationRebookRequestRequest
  ): Promise<ConversationRebookRequestDto> {
    const payload: CreateConversationRebookRequestRequest = {
      token: request.token?.trim() ?? request.token,
      message: request.message?.trim() || null,
    };

    const data = await fetchJson<ConversationRebookRequestDto>(
      `${API_BASE}/api/Conversations/rebook-request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    return normalizeConversationRebookRequest(data);
  },

  async getWalkerConversationRebookRequests(
    conversationId?: number
  ): Promise<ConversationRebookRequestDto[]> {
    const url =
      conversationId && conversationId > 0
        ? `${API_BASE}/api/Conversations/walker/${conversationId}/rebook-requests`
        : `${API_BASE}/api/Conversations/walker/rebook-requests`;

    const data = await fetchJsonWithAuth<ConversationRebookRequestDto[]>(url, {
      method: "GET",
    });

    return Array.isArray(data)
      ? data.map(normalizeConversationRebookRequest)
      : [];
  },

  async dismissWalkerConversationRebookRequest(
    rebookRequestId: number
  ): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/Conversations/rebook-request/${rebookRequestId}/dismiss`,
      {
        method: "POST",
      }
    );
  },

  async markWalkerConversationRebookRequestProposalCreated(
    rebookRequestId: number,
    bookingProposalId: number
  ): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/Conversations/rebook-request/${rebookRequestId}/mark-proposal-created`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bookingProposalId }),
      }
    );
  },

  async requestOwnerConversationAccess(
    request: RequestOwnerConversationAccessRequest
  ): Promise<OwnerConversationAccessResponseDto> {
    const payload: RequestOwnerConversationAccessRequest = {
      email: request.email?.trim() ?? request.email,
      maxConversations: request.maxConversations ?? 10,
    };

    return fetchJson<OwnerConversationAccessResponseDto>(
      `${API_BASE}/api/owner-conversations/request-link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
  },

  async getOwnerBookingsByToken(token: string): Promise<OwnerBookingListItemDto[]> {
    const encodedToken = encodeURIComponent(token.trim());

    const data = await fetchJson<OwnerBookingListItemDto[]>(
      `${API_BASE}/api/owner/bookings/by-token/${encodedToken}`,
      {
        method: "GET",
      }
    );

    return Array.isArray(data) ? data.map(normalizeOwnerBooking) : [];
  },

  async getWalkerInbox(
    includeClosed = false
  ): Promise<WalkerConversationListItemDto[]> {
    const qs = new URLSearchParams({
      includeClosed: includeClosed ? "true" : "false",
    });

    const data = await fetchJsonWithAuth<WalkerConversationListItemDto[]>(
      `${API_BASE}/api/Conversations/walker/inbox?${qs.toString()}`,
      {
        method: "GET",
      }
    );

    return Array.isArray(data)
      ? data.map(normalizeWalkerConversationListItem)
      : [];
  },

  async markWalkerConversationRead(conversationId: number): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/Conversations/walker/${conversationId}/mark-read`,
      {
        method: "POST",
      }
    );
  },

  async closeWalkerConversation(conversationId: number): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/Conversations/walker/${conversationId}/close`,
      {
        method: "POST",
      }
    );
  },

  async reopenWalkerConversation(conversationId: number): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/Conversations/walker/${conversationId}/reopen`,
      {
        method: "POST",
      }
    );
  },

  async requestWalkerMagicLink(
    request: RequestWalkerMagicLinkRequest
  ): Promise<{ message: string }> {
    return fetchJson<{ message: string }>(
      `${API_BASE}/api/walker-auth/request-link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      }
    );
  },

  async consumeWalkerMagicLink(
    request: ConsumeWalkerMagicLinkRequest
  ): Promise<WalkerSessionDto> {
    return fetchJsonWithAuth<WalkerSessionDto>(
      `${API_BASE}/api/walker-auth/consume`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      }
    );
  },

  async getWalkerMe(): Promise<WalkerSessionDto> {
    return fetchJsonWithAuth<WalkerSessionDto>(
      `${API_BASE}/api/walker-auth/me`,
      {
        method: "GET",
      }
    );
  },

  async signOutWalker(): Promise<{ message: string }> {
    return fetchJsonWithAuth<{ message: string }>(
      `${API_BASE}/api/walker-auth/signout`,
      {
        method: "POST",
      }
    );
  },

  async registerWalker(
    request: CreateWalkerRegistrationRequest
  ): Promise<WalkerRegistrationResultDto> {
    return fetchJson<WalkerRegistrationResultDto>(
      `${API_BASE}/api/Walkers/register`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      }
    );
  },

  async uploadWalkerPhotos(
    walkerProfileId: number,
    files: File[]
  ): Promise<WalkerPhotoDto[]> {
    const formData = new FormData();

    for (const file of files) {
      formData.append("files", file);
    }

    const res = await fetch(`${API_BASE}/api/Walkers/${walkerProfileId}/photos`, {
      method: "POST",
      body: formData,
    });

    const data = await readJson<WalkerPhotoDto[]>(res);
    return Array.isArray(data) ? data.map(normalizeWalkerPhoto) : [];
  },

  async deleteWalkerPhoto(
    walkerProfileId: number,
    photoId: number
  ): Promise<WalkerPhotoDto[]> {
    const data = await fetchJson<WalkerPhotoDto[]>(
      `${API_BASE}/api/Walkers/${walkerProfileId}/photos/${photoId}`,
      {
        method: "DELETE",
      }
    );

    return Array.isArray(data) ? data.map(normalizeWalkerPhoto) : [];
  },

  async reorderWalkerPhotos(
    walkerProfileId: number,
    items: ReorderWalkerPhotoItem[]
  ): Promise<WalkerPhotoDto[]> {
    const data = await fetchJson<WalkerPhotoDto[]>(
      `${API_BASE}/api/Walkers/${walkerProfileId}/photos/order`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      }
    );

    return Array.isArray(data) ? data.map(normalizeWalkerPhoto) : [];
  },

  async getConversationBookingProposals(
    conversationId: number
  ): Promise<BookingProposalDto[]> {
    const data = await fetchJsonWithAuth<BookingProposalDto[]>(
      `${API_BASE}/api/conversations/${conversationId}/booking-proposals`,
      {
        method: "GET",
      }
    );

    return Array.isArray(data) ? data.map(normalizeBookingProposal) : [];
  },

  async getConversationBookingProposalsByToken(
    token: string
  ): Promise<BookingProposalDto[]> {
    const encodedToken = encodeURIComponent(token.trim());

    const data = await fetchJson<BookingProposalDto[]>(
      `${API_BASE}/api/conversations/by-token/${encodedToken}/booking-proposals`,
      {
        method: "GET",
      }
    );

    return Array.isArray(data) ? data.map(normalizeBookingProposal) : [];
  },

  async getWalkerBookings(): Promise<BookingProposalDto[]> {
    const data = await fetchJsonWithAuth<BookingProposalDto[]>(
      `${API_BASE}/api/walker/bookings`,
      {
        method: "GET",
      }
    );

    return Array.isArray(data) ? data.map(normalizeBookingProposal) : [];
  },

  async createConversationBookingProposal(
    conversationId: number,
    request: CreateConversationBookingProposalRequest
  ): Promise<BookingProposalDto> {
    const payload: CreateConversationBookingProposalRequest = {
      serviceDateLocal: request.serviceDateLocal,
      startTimeLocal: request.startTimeLocal ?? null,
      endTimeLocal: request.endTimeLocal ?? null,
      serviceType: request.serviceType?.trim() ?? request.serviceType,
      dogCount: request.dogCount ?? null,
      locationSummary: request.locationSummary?.trim() || null,
      notes: request.notes?.trim() || null,
      grossAmountPence: request.grossAmountPence,
    };

    const data = await fetchJsonWithAuth<BookingProposalDto>(
      `${API_BASE}/api/conversations/${conversationId}/booking-proposals`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    return normalizeBookingProposal(data);
  },

  async createBookingProposalCheckoutSession(
    bookingProposalId: number,
    request?: CreateBookingProposalCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponseDto> {
    return fetchJson<CreateCheckoutSessionResponseDto>(
      `${API_BASE}/api/booking-proposals/${bookingProposalId}/checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request ?? {}),
      }
    );
  },

  async createBookingProposalCheckoutSessionByToken(
    bookingProposalId: number,
    request: CreateBookingProposalCheckoutSessionByTokenRequest
  ): Promise<CreateCheckoutSessionResponseDto> {
    return fetchJson<CreateCheckoutSessionResponseDto>(
      `${API_BASE}/api/booking-proposals/${bookingProposalId}/checkout-session/by-token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: request.token?.trim(),
          successUrl: request.successUrl,
          cancelUrl: request.cancelUrl,
        }),
      }
    );
  },

  async completeBookingProposal(
    bookingProposalId: number
  ): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/booking-proposals/${bookingProposalId}/complete`,
      {
        method: "POST",
      }
    );
  },

  async cancelBookingProposal(
    bookingProposalId: number
  ): Promise<void> {
    await fetchNoContentWithAuth(
      `${API_BASE}/api/booking-proposals/${bookingProposalId}/cancel`,
      {
        method: "POST",
      }
    );
  },

  async getAdminStaleConversationPreview(
    staleAfterDays: number
  ): Promise<AdminStaleConversationDto[]> {
    const qs = new URLSearchParams({
      staleAfterDays: String(staleAfterDays),
    });

    return fetchJson<AdminStaleConversationDto[]>(
      `${API_BASE}/api/admin/conversations/stale-preview?${qs.toString()}`,
      {
        method: "GET",
        headers: buildAdminHeaders(),
      }
    );
  },

  async autoCloseAdminStaleConversations(
    staleAfterDays: number
  ): Promise<AdminAutoCloseStaleConversationsResponseDto> {
    return fetchJson<AdminAutoCloseStaleConversationsResponseDto>(
      `${API_BASE}/api/admin/conversations/auto-close-stale`,
      {
        method: "POST",
        headers: buildAdminHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ staleAfterDays }),
      }
    );
  },

  async createWalkerStripeConnectLink(options?: {
    walkerProfileId?: number;
    walkerEmail?: string;
  }): Promise<{
    url: string;
    stripeAccountId?: string | null;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
    detailsSubmitted?: boolean;
    requiresOnboarding?: boolean;
  }> {
    const qs = new URLSearchParams();

    if (options?.walkerProfileId && options.walkerProfileId > 0) {
      qs.set("walkerProfileId", String(options.walkerProfileId));
    }

    if (options?.walkerEmail?.trim()) {
      qs.set("walkerEmail", options.walkerEmail.trim());
    }

    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    return fetchJsonWithAuth(
      `${API_BASE}/api/walker/stripe/connect${suffix}`,
      {
        method: "POST",
      }
    );
  },

  async getWalkerStripeStatus(): Promise<{
    stripeConnected: boolean;
    stripeAccountId?: string | null;
    chargesEnabled?: boolean;
    payoutsEnabled?: boolean;
    detailsSubmitted?: boolean;
    onboardingComplete?: boolean;
    requiresOnboarding?: boolean;
  }> {
    return fetchJsonWithAuth(`${API_BASE}/api/walker/stripe/status`, {
      method: "GET",
    });
  },
};